/**
 * Fog of war time machine: the board right after event `at`, as this player
 * could see it then (GET /games/:id/snapshot, docs/fog-of-war/README.md).
 * A fog game's client holds only its own redacted log, so it can't rebuild
 * history itself. A snapshot at a given index never changes (vision is fixed
 * when the event is saved), so it is cached for the life of the screen.
 */
import React from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WireTerritory } from '@warcarp/api-types'
import { apiGet } from '../lib/api'

type FogSnapshot = { at: number; territories: WireTerritory[] }

const snapshotQuery = (gameId: string, at: number) => ({
  queryKey: ['games', gameId, 'snapshot', at],
  queryFn: () => apiGet<FogSnapshot>(`/api/v1/games/${gameId}/snapshot?at=${at}`),
  staleTime: Infinity,
})

/**
 * `data` is the snapshot at `at`, or, while moving between snapshots, the
 * previous one until the next lands, so the screen never falls back to the
 * live board mid-session. On first open it stays undefined until the
 * snapshot arrives (the screen switches once, from live to snapshot), and a
 * snapshot from an earlier session is never reused. The neighbouring indices
 * are prefetched so Prev / Next land instantly.
 */
export function useFogSnapshot(gameId: string | undefined, at: number | null, enabled: boolean) {
  const queryClient = useQueryClient()
  const active = enabled && !!gameId && at !== null
  React.useEffect(() => {
    if (!active) return
    for (const near of [at! - 1, at! + 1]) {
      if (near >= -1) void queryClient.prefetchQuery(snapshotQuery(gameId!, near))
    }
  }, [active, gameId, at, queryClient])
  const query = useQuery<FogSnapshot>({
    ...snapshotQuery(gameId ?? '', at ?? -1),
    enabled: active,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
  // Whether this snapshot session has shown one yet.
  const shown = React.useRef(false)
  if (!active) shown.current = false
  const exact = active && query.data?.at === at
  if (exact) shown.current = true
  return { data: exact || (active && shown.current) ? query.data : undefined }
}
